/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** The three kinds of row a plan's card can hold. */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import type {
  TeamPendingInvite, TeamPerson,
} from '@/services/requests/housien-services/AgenciesService.team';
import { Avatar, DotsIcon, QuotaCell, RolePill } from './TeamScreen.parts';
import { initialsOf } from './team-rows';

/**
 * The grid every row lines up on.
 *
 * Written once and shared, because the point of the grid is that the avatars,
 * the names, the meters and the menus line up down the whole card — a row
 * that laid itself out would break exactly that.
 */
const GRID = '30px minmax(0,1fr) minmax(0,260px) auto';
const META_GRID = '120px 44px 28px';

/**
 * Row style.
 */
const rowStyle = (last: boolean, mobile: boolean): React.CSSProperties => ({
  position: 'relative',
  display: mobile ? 'flex' : 'grid',
  gridTemplateColumns: mobile ? undefined : GRID,
  flexWrap: mobile ? 'wrap' : undefined,
  alignItems: 'center',
  gap: 'var(--space-3) var(--space-4)',
  padding: 'var(--space-3) var(--space-5)',
  borderBottom: last ? 'none' : '1px solid var(--border-ultra-subtle)',
});

const nameStyle: React.CSSProperties = {
  fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)',
  color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const subStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)', color: 'var(--text-tertiary-soft)',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};

/** The meta column: role, where they stand, and the way to act on them. */
const Meta: React.FC<{
  role: string | null;
  roleLabel: string;
  state: string;
  stateAttention?: boolean;
  onMenu?: () => void;
  testId: string;
  mobile: boolean;
}> = ({ role, roleLabel, state, stateAttention, onMenu, testId, mobile }) => {
  const { t } = useTranslation('features/agency-settings');
  return (
    <div
      style={{
        display: mobile ? 'flex' : 'grid',
        gridTemplateColumns: mobile ? undefined : META_GRID,
        alignItems: 'center', gap: 'var(--space-2)', flex: 'none',
        marginLeft: mobile ? 0 : 'auto',
      }}
    >
      <RolePill role={role} label={roleLabel} />
      <Text
        component="span"
        style={{
          width: mobile ? undefined : 44, textAlign: mobile ? 'left' : 'right',
          fontSize: 'var(--text-meta)',
          color: stateAttention ? 'var(--accent-mostarda)' : 'var(--text-tertiary-soft)',
        }}
      >
        {state}
      </Text>
      {onMenu ? (
        <UnstyledButton
          onClick={onMenu}
          aria-label={t('v2.team.rowActions')}
          data-testid={`team-member-menu-${testId}`}
          style={{
            width: 28, height: 28, borderRadius: 'var(--radius-md)', background: 'none',
            cursor: 'pointer', color: 'var(--text-tertiary-soft)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <DotsIcon />
        </UnstyledButton>
      ) : (
        // The owner has no menu, and the column still has to hold its width
        // or every row below would shift left by 28px.
        <div style={{ width: 28 }} />
      )}
    </div>
  );
};

/** Somebody seated, with the allowance their seat carries. */
export const PersonRow: React.FC<{
  person: TeamPerson;
  roleLabel: string;
  isYou: boolean;
  last: boolean;
  mobile: boolean;
  onMenu?: () => void;
  children?: React.ReactNode;
}> = ({ person, roleLabel, isYou, last, mobile, onMenu, children }) => {
  const { t } = useTranslation('features/agency-settings');
  const owner = person.role === 'owner';
  return (
    <div
      style={rowStyle(last, mobile)}
      data-testid={`team-member-${person.email}`}
    >
      <Avatar initials={initialsOf(person)} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Text component="span" style={nameStyle}>
          {`${person.first_name ?? ''} ${person.last_name ?? ''}`.trim() || person.email}
        </Text>
        <Text component="span" style={subStyle}>{person.email}</Text>
        {!person.plan_id && (
          <Text
            component="span"
            data-testid={`seat-plan-unset-${person.email}`}
            style={{
              fontSize: 'var(--text-tag)', fontWeight: 'var(--font-bold)',
              color: 'var(--accent-mostarda)',
            }}
          >
            {t('v2.team.seatPlanUnset')}
          </Text>
        )}
      </div>
      <div
        style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
          gap: 'var(--space-4)', minWidth: 0,
          flex: mobile ? '1 1 100%' : undefined,
          order: mobile ? 1 : undefined,
        }}
      >
        <QuotaCell label={t('v2.team.quotaInteractions')} quota={person.interaction} />
        <QuotaCell label={t('v2.team.quotaSearches')} quota={person.partnership_search} />
      </div>
      <Meta
        role={person.role}
        roleLabel={owner ? t('v2.team.roleOwner') : roleLabel}
        state={isYou ? t('v2.team.stateYou') : t('v2.team.stateActive')}
        onMenu={owner ? undefined : onMenu}
        testId={person.email}
        mobile={mobile}
      />
      {children}
    </div>
  );
};

/** An invitation still holding its seat. */
export const PendingRow: React.FC<{
  invite: TeamPendingInvite;
  roleLabel: string;
  last: boolean;
  mobile: boolean;
  onMenu?: () => void;
  children?: React.ReactNode;
}> = ({ invite, roleLabel, last, mobile, onMenu, children }) => {
  const { t } = useTranslation('features/agency-settings');
  const sent = invite.created_at
    ? new Date(invite.created_at).toLocaleDateString()
    : null;
  return (
    <div style={rowStyle(last, mobile)} data-testid={`team-member-${invite.email}`}>
      <Avatar initials={initialsOf(invite)} muted />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Text component="span" style={nameStyle}>{invite.email}</Text>
        <Text component="span" style={subStyle}>
          {sent ? t('v2.team.inviteSentOn', { date: sent }) : t('v2.team.inviteSent')}
        </Text>
      </div>
      {/* No meters: nobody has drawn anything yet, and a full bar beside a
          name that has not arrived reads as somebody who is already here. */}
      <Text
        component="span"
        style={{
          fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)', minWidth: 0,
          flex: mobile ? '1 1 100%' : undefined, order: mobile ? 1 : undefined,
        }}
      >
        {t('v2.team.noUsageUntilAccepted')}
      </Text>
      <Meta
        role={invite.role}
        roleLabel={roleLabel}
        state={t('v2.team.statePending')}
        stateAttention
        onMenu={onMenu}
        testId={invite.email}
        mobile={mobile}
      />
      {children}
    </div>
  );
};

/** A seat nobody sits in, and the way to fill it. */
export const VacantRow: React.FC<{
  planId: string;
  index: number;
  last: boolean;
  mobile: boolean;
  onInvite: () => void;
}> = ({ planId, index, last, mobile, onInvite }) => {
  const { t } = useTranslation('features/agency-settings');
  return (
    <div style={rowStyle(last, mobile)} data-testid={`team-free-seat-${planId}-${index}`}>
      <div
        style={{
          width: 30, height: 30, borderRadius: 'var(--radius-full)', flex: 'none',
          border: '1px dashed var(--border-medium)',
        }}
      />
      <Text component="span" style={{ ...subStyle, flex: 1 }}>
        {t('v2.team.freeSeat')}
      </Text>
      <div style={{ minWidth: 0 }} />
      <UnstyledButton
        onClick={onInvite}
        style={{
          marginLeft: mobile ? 0 : 'auto', height: 28, padding: '0 var(--space-3)',
          borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-medium)',
          background: 'var(--surface-primary)', cursor: 'pointer',
          fontSize: 'var(--text-meta)', fontWeight: 'var(--font-semibold)',
          color: 'var(--text-primary)',
        }}
      >
        {t('v2.team.inviteAction')}
      </UnstyledButton>
    </div>
  );
};
